import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import { fetchLoyaltyDashboard, fetchLoyaltyStatus, fetchLoyaltyPointsHistory } from '../features/loyalty/loyalty.slice';
import LoyaltyPromotionBanner from './loyalty-promotion-banner.component';
import LoyaltyRulesSection from './loyalty-rules-section.component';
import LoyaltyStatusCard from './loyalty-status-card.component';
import LoyaltyRewardCard from './loyalty-reward-card.component';
import LoyaltyPointsActivity from './loyalty-points-activity.component';
import InviteFriendModal from './invite-friend-modal.component';
import LoyaltyActionsSection from './loyalty-actions-section.component';
import { MEDAL_LEVELS } from '../../common/loyaltyConstants';
import '../scss/loyalty-program.component.scss';
import { Sidebar } from './Sidebar/index';

interface LoyaltyProgramComponentProps {
    userPoints?: number;
    currentLevel?: string;
}
const LoyaltyProgramComponent: React.FC<LoyaltyProgramComponentProps> = ({
    userPoints = 0,
    currentLevel = 'Iron'
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const loyaltyData = useAppSelector((state) => state.loyalty?.loyaltyData);
    const loyaltyStatusData = useAppSelector((state) => state.loyalty?.loyaltyStatusData);
    const discountPercentage = loyaltyStatusData?.response.discountPercentage

    const [isPointsActivityOpen, setIsPointsActivityOpen] = useState(false);
    const [isInviteFriendOpen, setIsInviteFriendOpen] = useState(false);

    // Fetch loyalty dashboard data on component mount
    useEffect(() => {
        dispatch(fetchLoyaltyDashboard());
        dispatch(fetchLoyaltyStatus());
    }, [dispatch]);


    // Use API data if available, otherwise fallback to static data
    const getMedalLevels = () => {
        // Check if we have API data with response array
        if (loyaltyData?.response && loyaltyData.response.length > 0) {
            // Transform API response to medal levels format
            const transformedLevels = loyaltyData.response.map(tier => ({
                type: tier.name,
                name: `LOYALTY_PROGRAM_${tier.name.toUpperCase()}_LEVEL`,
                description: `LOYALTY_PROGRAM_${tier.name.toUpperCase()}_LEVEL_DESC`,
                pointsRequired: tier.pointsRequired,
                discount: tier.discountPercentage
            }));
            return transformedLevels;
        }
        return MEDAL_LEVELS;
    };

    // Use dynamic user points from API if available, otherwise use prop or default
    const getCurrentUserPoints = () => {
        // Priority: API data > component prop > default value
        if (loyaltyStatusData?.response?.pointsEarned !== undefined) {
            return loyaltyStatusData.response.pointsEarned;
        }
        return userPoints || 0;
    };

    const getCurrentMedalType = (points: number): string => {
        // Use API data if available, otherwise fallback to static logic
        if (loyaltyData?.response && loyaltyData.response.length > 0) {
            // Sort tiers by points required (ascending) to find the current level
            const sortedTiers = [...loyaltyData.response].sort((a, b) => a.pointsRequired - b.pointsRequired);

            // Find the highest tier the user has achieved
            let currentTier = sortedTiers[0]; // Default to lowest tier
            for (const tier of sortedTiers) {
                if (points >= tier.pointsRequired) {
                    currentTier = tier;
                } else {
                    break;
                }
            }
            return currentTier.name;
        }

        // Fallback to static logic if no API data
        if (points >= 200000) return 'Platinum';
        if (points >= 100000) return 'Gold';
        if (points >= 50000) return 'Silver';
        if (points >= 25000) return 'Bronze';
        return 'Iron';
    };
    const getMaxPointsForNextLevel = (points: number): number => {
        // Use API data if available
        if (loyaltyData?.response && loyaltyData.response.length > 0) {
            const sortedTiers = [...loyaltyData.response].sort((a, b) => a.pointsRequired - b.pointsRequired);

            // Find the next tier above user's current points
            for (const tier of sortedTiers) {
                if (points < tier.pointsRequired) {
                    return tier.pointsRequired;
                }
            }

            // If user has achieved the highest tier, return that tier's points
            return sortedTiers[sortedTiers.length - 1].pointsRequired;
        }

        // Fallback to static logic if no API data
        if (points >= 200000) return 200000; // Already at max level
        if (points >= 1000000) return 2000000; // Next: Platinum
        if (points >= 500000) return 1000000;  // Next: Gold
        if (points >= 250000) return 500000;   // Next: Silver
        return 250000; // Next: Bronze
    };
    const isLevelAchieved = (levelType: string, points: number): boolean => {
        // Use the user's current points to determine their medal level
        const currentMedal = getCurrentMedalType(points);
        const levelOrder = ['Iron', 'Bronze', 'Silver', 'Gold', 'Platinum'];
        const currentIndex = levelOrder.indexOf(currentMedal);
        const levelIndex = levelOrder.indexOf(levelType);
        return levelIndex <= currentIndex;
    };
    const handlePointsActivityClick = () => {
        setIsPointsActivityOpen(true);
        // Fetch fresh points history data when opening the modal
        dispatch(fetchLoyaltyPointsHistory({ pageNumber: 1, pageSize: 50 }));
    };
    const handleClosePointsActivity = () => {
        setIsPointsActivityOpen(false);
    };
    const handleInviteFriendClick = () => {
        setIsInviteFriendOpen(true);
    };
    const handleCloseInviteFriend = () => {
        setIsInviteFriendOpen(false);
    };
    const currentPoints = getCurrentUserPoints();
    const medalLevels = getMedalLevels();

    return (
        <>
            <div className="loyalty-program-content mb-3 ">

                <Container className='px-0 px-sm-3'>
                    <section className="loyalty-status-section ">

                        <Row className="status-row flex">
                            <div className='d-sm-block d-none'>
                                <LoyaltyStatusCard
                                    discountPercentage={discountPercentage}
                                    userPoints={currentPoints}
                                    currentLevel={getCurrentMedalType(currentPoints)}
                                    maxPoints={getMaxPointsForNextLevel(currentPoints)}
                                />
                            </div>

                            <Col className='px-3 order-1 order-md-2'>
                                <div className="loyalty-program-info">
                                    <div className="program-title-row d-flex justify-content-between align-items-start my-3">
                                        <h2 className="program-title text-start text-sm-start ">
                                            {t('LOYALTY_PROGRAM_INVOZIO_LOYALTY_PROGRAM')}
                                        </h2>
                                        <button type="button" className="program-cta-btn" onClick={handlePointsActivityClick}>
                                            <span>{t('LOYALTY_POINTS_ACTIVITY_TITLE')}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none" className="arrow-icon" aria-hidden="true" focusable="false">
                                                <path d="M4.03076 9H13.7808M10.0308 4.5L14.0004 8.46967C14.2933 8.76256 14.2933 9.23744 14.0004 9.53033L10.0308 13.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className='d-sm-none d-flex justify-content-center mb-4'>
                                        <LoyaltyStatusCard
                                            discountPercentage={discountPercentage}
                                            userPoints={currentPoints}
                                            currentLevel={getCurrentMedalType(currentPoints)}
                                            maxPoints={getMaxPointsForNextLevel(currentPoints)}
                                        />
                                    </div>

                                    <div className="program-section-divider" role="presentation"></div>

                                    <div className="reward-milestones">
                                        <h4 className="reward-milestones-title">
                                            {t('LOYALTY_PROGRAM_REWARD_MILESTONES')}
                                        </h4>
                                        <Row className="reward-milestones-row flex-nowrap">
                                            {medalLevels.map((level) => (
                                                <Col key={level.type} xs="auto">
                                                    <LoyaltyRewardCard
                                                        medalType={level.type as any}
                                                        level={t(level.name)}
                                                        description={t(level.description)}
                                                        pointsRequired={level.pointsRequired}
                                                        discount={level.discount}
                                                        isAchieved={isLevelAchieved(level.type, currentPoints)} />
                                                </Col>
                                            ))}

                                        </Row>
                                        <div className='show-1892-up'>
                                            <LoyaltyActionsSection onInviteFriendClick={handleInviteFriendClick} />
                                        </div>

                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>
                    <div className='hide-1440-up mb-2'>
                        <LoyaltyActionsSection onInviteFriendClick={handleInviteFriendClick} />
                    </div>
                    <section className="loyalty-rules-section">
                        <div className="custom-grid rules-grid">
                            <div className='rules-content-wrapper px-3'>
                                <LoyaltyRulesSection />
                            </div>
                            <div className='rules-banner-wrapper  d-none d-xl-block pt-3'>
                                <LoyaltyPromotionBanner />
                            </div>
                        </div>
                    </section>
                </Container>

                {isPointsActivityOpen && (
                    <LoyaltyPointsActivity onClose={handleClosePointsActivity} />
                )}
                {isInviteFriendOpen && (
                    <InviteFriendModal onClose={handleCloseInviteFriend} />
                )}
            </div>
        </>
    );
};
export default LoyaltyProgramComponent;
